/**
 * ReportScopeIndicator / ScopeBadge / OrgBaselineNote
 *
 * Item B (Change C2) — telling the reader WHOSE numbers these are.
 *
 * A manager's reports cover themselves plus their active direct reports; an
 * account_admin reads the whole tenant. "Average progress 62%" is unreadable
 * without knowing which of those it is, and a manager who assumes it is the
 * organization figure will draw the wrong conclusion from a correct number.
 *
 * These components render a ResolvedScope — already interpreted by
 * `hooks/useReportData.ts`. They never see the raw API `scope` field, and they
 * render NOTHING for an unscoped caller, so an account_admin's page looks
 * exactly as it did before Item B.
 */

import React from 'react';
import { clsx } from 'clsx';
import { Users, Building2, User } from 'lucide-react';
import type { ResolvedScope } from '../../hooks/useReportData';

export interface ReportScopeIndicatorProps {
  /** Resolved scope for the caller (not for one card). */
  scope: ResolvedScope;
  /** Additional class names */
  className?: string;
}

/**
 * Persistent page-level indicator, shown beside the date-range selector.
 *
 * Deliberately persistent rather than per-card: it sits next to the other
 * control that changes what the numbers mean, and it cannot be scrolled past.
 */
export const ReportScopeIndicator: React.FC<ReportScopeIndicatorProps> = ({
  scope,
  className,
}) => {
  if (scope.kind === 'unscoped') return null;

  const Icon = scope.isSelfOnly ? User : Users;

  return (
    <div
      className={clsx(
        'flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-white',
        className
      )}
      title={scope.description ?? undefined}
      data-testid="report-scope-indicator"
    >
      <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <div className="leading-tight">
        <p className="text-sm font-medium">{scope.label}</p>
        <p className="text-xs text-stone-300">{scope.description}</p>
      </div>
    </div>
  );
};

export interface ScopeBadgeProps {
  /** Resolved scope for THIS figure — not necessarily the page's. */
  scope: ResolvedScope;
  /** Additional class names */
  className?: string;
}

/**
 * Inline badge for a single card or a single group of figures.
 *
 * Only worth rendering where a card diverges from the page indicator — the
 * organization-wide engagement cards, and the two halves of the collaboration
 * card. Sprinkling it everywhere else would just repeat the page label.
 */
export const ScopeBadge: React.FC<ScopeBadgeProps> = ({ scope, className }) => {
  if (scope.kind === 'unscoped') return null;

  const isOrg = scope.kind === 'organization';
  const Icon = isOrg ? Building2 : scope.isSelfOnly ? User : Users;

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        isOrg ? 'bg-stone-100 text-stone-600' : 'bg-primary-50 text-primary-700',
        className
      )}
      title={scope.description ?? undefined}
      data-testid="report-scope-badge"
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {scope.label}
    </span>
  );
};

export interface OrgBaselineNoteProps {
  /** Formatted organization-wide figure, e.g. "62.4%". */
  value: string;
  /** What the figure is, e.g. "avg progress". */
  label: string;
  /** Additional class names */
  className?: string;
}

/**
 * The organization comparison for a scoped figure.
 *
 * Item B narrows a manager's rows; `orgBaseline` is what gives back the
 * context that narrowing removed — it is the whole reason D-B1's shrunken row
 * lists were acceptable. It is computed with NO filters applied, so it must
 * never be presented as responding to the filter panel.
 */
export const OrgBaselineNote: React.FC<OrgBaselineNoteProps> = ({
  value,
  label,
  className,
}) => (
  <p
    className={clsx('flex items-center gap-1 text-xs text-stone-500', className)}
    title="Organization-wide figure for this period. Not affected by the filter panel."
    data-testid="org-baseline-note"
  >
    <Building2 className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
    <span>
      Organization: <span className="font-medium text-stone-700">{value}</span> {label}
    </span>
  </p>
);

export default ReportScopeIndicator;
